import {
  Button,
  Text,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Navigation24Filled,
  Navigation24Regular,
  bundleIcon,
} from '@fluentui/react-icons';
import React from 'react';

import { EnvironmentBadge } from './EnvironmentBadge.js';
import { NavSection as NavSectionComponent } from './nav-section';
import { useNavigationMenuStyles } from './navigation-menu.style';

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
const NavIcon = bundleIcon(Navigation24Filled, Navigation24Regular);

export interface NavItem {
  key: string;
  label: string;
  description?: string;
  icon?: React.ReactElement;
  path?: string;
  end?: boolean;
  children?: NavItem[];
}

export interface NavSectionData {
  header: string;
  items: NavItem[];
}

interface NavigationMenuProps {
  isOpen: boolean;
  brandName: string;
  environment?: string;
  webVersion?: string;
  serviceVersion?: string;
  isServiceVersionLoading?: boolean;
  sections: NavSectionData[];
  selectedPath?: string;
  onToggle: () => void;
}

const NavigationMenu: React.FC<NavigationMenuProps> = ({
  isOpen,
  brandName,
  environment,
  webVersion,
  serviceVersion,
  isServiceVersionLoading,
  sections,
  selectedPath = '',
  onToggle,
}) => {
  const styles = useNavigationMenuStyles();

  return (
    <aside
      className={mergeClasses(
        styles.sidebar,
        isOpen ? styles.sidebarOpen : styles.sidebarCollapsed,
      )}
    >
      <div className={styles.sidebarHeader}>
        <Tooltip content="Toggle navigation pane" relationship="label">
          <Button
            appearance="transparent"
            icon={<NavIcon />}
            onClick={onToggle}
            aria-expanded={isOpen}
            className={styles.navToggle}
          />
        </Tooltip>
        {isOpen && (
          <div className={styles.brandGroup}>
            <Text className={styles.brandText}>{brandName}</Text>
            {environment && webVersion && (
              <EnvironmentBadge
                environment={environment}
                webVersion={webVersion}
                serviceVersion={serviceVersion}
                isServiceVersionLoading={isServiceVersionLoading}
              />
            )}
          </div>
        )}
      </div>

      <nav className={styles.nav}>
        {sections.map((section, si) => (
          <React.Fragment key={section.header}>
            {si > 0 && <div className={styles.navDivider} />}
            <NavSectionComponent
              header={section.header}
              items={section.items}
              isOpen={isOpen}
              selectedPath={selectedPath}
            />
          </React.Fragment>
        ))}
      </nav>
    </aside>
  );
};

export { NavigationMenu };
